/**
 * The minimised download, pinned to the corner of every page: the twin of the
 * upload dock. Hidden when the queue is empty, and never on the Download Center
 * itself. Sits above the upload dock when both are showing.
 */
import { Link, useLocation } from 'react-router-dom';
import { Download, Check, AlertTriangle } from 'lucide-react';
import { useDownloadCenter } from '@/lib/download-center';
import { useUploadCenter, formatBytes, formatSpeed, formatEta } from '@/lib/upload-center';
import { cn } from '@/lib/cn';

export default function DownloadDock() {
  const { active, queued, failed, overallPct, pendingBytes, jobs, doneThisRun, totalThisRun,
          bytesPerSec, etaSeconds, folderName } = useDownloadCenter();
  const up = useUploadCenter();
  const { pathname } = useLocation();

  const live = active + queued;
  if (live === 0 && failed === 0) return null;
  if (pathname === '/downloads') return null;
  if (jobs.length === 0) return null;
  const uploadDockShowing = pathname !== '/uploads' && up.jobs.length > 0 && (up.active + up.queued > 0 || up.failed > 0);
  const browserMode = jobs.some((j) => j.mode === 'browser' && (j.state === 'queued' || j.state === 'downloading'));

  return (
    <Link
      to="/downloads"
      data-testid="download-dock"
      className={cn(
        'fixed right-4 z-40 w-64 rounded-xl border bg-bg-card shadow-lg px-3 py-2.5 transition',
        uploadDockShowing ? 'bottom-[7.5rem]' : 'bottom-4',
        failed > 0 && live === 0 ? 'border-red-500/40' : 'border-slate-800 hover:border-accent-400/50',
      )}
    >
      <div className="flex items-center gap-2">
        {live > 0
          ? <Download size={14} className="text-accent-400 shrink-0" />
          : failed > 0
            ? <AlertTriangle size={14} className="text-red-400 shrink-0" />
            : <Check size={14} className="text-emerald-400 shrink-0" />}
        <span className="text-xs text-muted-strong font-medium">
          {live > 0 ? `Downloading ${live} file${live === 1 ? '' : 's'}` : `${failed} failed`}
        </span>
        {live > 0 && overallPct !== null && !browserMode && (
          <span className="ml-auto text-[11px] text-accent-400">{overallPct}%</span>
        )}
      </div>

      {live > 0 && overallPct !== null && !browserMode && (
        <div className="h-1 rounded bg-slate-800 mt-2 overflow-hidden">
          <div className="h-full bg-accent-500 transition-all" style={{ width: `${overallPct}%` }} />
        </div>
      )}

      {live > 0 && (
        <div className="text-[11px] text-muted mt-1.5 space-y-0.5">
          <div>{doneThisRun} of {totalThisRun}{browserMode ? ' handed to the browser' : ` - ${formatBytes(pendingBytes)} left`}{failed ? ` - ${failed} failed` : ''}</div>
          {!browserMode && (bytesPerSec || etaSeconds !== null) && (
            <div className="text-muted-strong">
              {formatSpeed(bytesPerSec)}
              {bytesPerSec && etaSeconds !== null ? ' · ' : ''}
              {etaSeconds !== null ? `${formatEta(etaSeconds)} left` : ''}
            </div>
          )}
          {folderName && !browserMode && <div className="truncate">into {folderName}</div>}
        </div>
      )}
    </Link>
  );
}
